/**
 * Lightweight token / context estimates for the composer meter.
 * Not exact tokenizer parity — good enough for UX pressure signals.
 */

import type { ChatMessage } from '@/hooks/useShakespeare';
import type { SessionBlockInput } from '@/lib/nostr/sessionBlock';
import type { Message, MessageAttachment, Project, WorkspaceSettings } from './types';
import { buildSystemContent } from './prompt';

/** ~4 chars per token is a common English/code heuristic. */
export function estimateTokensFromText(text: string): number {
  if (!text) return 0;
  return Math.ceil(text.length / 4);
}

export function estimateAttachmentTokens(att: MessageAttachment): number {
  if (att.kind === 'image') {
    // Vision models bill images heavily; rough fixed cost + data length
    const dataLen = att.data?.length ?? 0;
    return 800 + Math.ceil(dataLen / 12);
  }
  return estimateTokensFromText(att.data || '') + 20;
}

export function estimateMessageTokens(msg: Pick<Message, 'content' | 'attachments'>): number {
  let n = estimateTokensFromText(msg.content || '');
  for (const a of msg.attachments ?? []) {
    n += estimateAttachmentTokens(a);
  }
  return n;
}

export function estimateApiMessagesTokens(messages: ChatMessage[]): number {
  let n = 0;
  for (const m of messages) {
    if (typeof m.content === 'string') {
      n += estimateTokensFromText(m.content);
    } else if (Array.isArray(m.content)) {
      for (const part of m.content) {
        if (part.type === 'text' && part.text) n += estimateTokensFromText(part.text);
        if (part.type === 'image_url') n += 850;
      }
    }
    n += 4; // role overhead
  }
  return n;
}

export function formatTokenCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/** Resolve context window from model metadata with a sensible default. */
export function resolveContextWindow(
  model: { context_window?: number } | null | undefined,
): number {
  const w = model?.context_window;
  if (typeof w === 'number' && w > 0) return w;
  return 128_000;
}

/**
 * Estimate total tokens that will be sent on the next completion
 * (system stack + history + optional draft).
 */
export function estimateOutgoingTokens(opts: {
  project: Project | null | undefined;
  chat?: import('./types').Chat | null;
  chatTitle?: string;
  settings?: Pick<
    WorkspaceSettings,
    'globalInstructions' | 'defaultPersonaId' | 'groundedOnly'
  > | null;
  history: Message[];
  draft?: string;
  draftAttachments?: MessageAttachment[];
  /** Session block input — pass so the meter counts the app-rules layer */
  session?: SessionBlockInput | null;
  /** Extra omit keys (merged with chat.promptOmit) */
  omit?: string[] | null;
}): number {
  const omitAttachments =
    opts.omit?.includes('attachments') ||
    opts.chat?.promptOmit?.includes('attachments');
  const system = buildSystemContent(opts.project, opts.chat ?? null, {
    settings: opts.settings,
    session: opts.session ?? null,
    omit: opts.omit,
  });
  let n = estimateTokensFromText(system) + 8;
  for (const m of opts.history) {
    if (m.role === 'system') continue;
    n += estimateMessageTokens(m) + 4;
  }
  if (opts.draft?.trim()) n += estimateTokensFromText(opts.draft);
  if (!omitAttachments) {
    for (const a of opts.draftAttachments ?? []) {
      n += estimateAttachmentTokens(a);
    }
  }
  return n;
}

export type ContextMeterLevel = 'ok' | 'warn' | 'critical';

export function contextMeterLevel(used: number, window: number): ContextMeterLevel {
  if (window <= 0) return 'ok';
  const ratio = used / window;
  if (ratio >= 0.92) return 'critical';
  if (ratio >= 0.75) return 'warn';
  return 'ok';
}
